import { ColumnDef, Row } from "@tanstack/react-table";
import React, { memo, useCallback, useMemo } from "react";
import { Vehicle } from "../../../constants/common";
import { Button } from "../../button";
import { useTranslation } from "react-i18next";
import TableWrapper from "../TableWrapper";

interface PermitVehiclesTableProps {
  addedVehicles: Vehicle[];
  setAddedVehicles: React.Dispatch<React.SetStateAction<Vehicle[]>>;
  setEditingVehicle: React.Dispatch<React.SetStateAction<Vehicle | null>>;
  setIsAddVehicleFormVisible: React.Dispatch<React.SetStateAction<boolean>>;
  onFormReset?: () => void;
  plateSource:
    | { labelEn: string; labelAr: string; value: number }[]
    | undefined;
  plateLetters:
    | { labelEn: string; labelAr: string; value: number }[]
    | undefined;
  driversNameOptions: {
    labelEn: string;
    labelAr: string;
    value: string;
  }[];
  isFieldsDisabled: boolean;
}

const PermitVehiclesTable = ({
  addedVehicles,
  setAddedVehicles,
  setEditingVehicle,
  setIsAddVehicleFormVisible,
  onFormReset,
  plateSource,
  plateLetters,
  driversNameOptions,
  isFieldsDisabled,
}: PermitVehiclesTableProps) => {
  const { t, i18n } = useTranslation();

  const handleEdit = useCallback(
    (vehicle: Vehicle) => {
      setEditingVehicle(vehicle);
      setIsAddVehicleFormVisible(true);
    },
    [setEditingVehicle, setIsAddVehicleFormVisible]
  );

  const handleDelete = useCallback(
    (id: string) => {
      setAddedVehicles((prev) => prev.filter((vehicle) => vehicle.id !== id));
      setEditingVehicle(null);
      // Reset the form when a vehicle is deleted
      if (onFormReset) {
        onFormReset();
      }
    },
    [setAddedVehicles, setEditingVehicle, onFormReset]
  );

  const columns: ColumnDef<Vehicle>[] = useMemo(
    () => [
      {
        accessorKey: "plateType",
        header: t("plateType"),
        cell: ({ row }) => {
          const plateType = plateSource?.find(
            (item) => item.value === Number(row.original.plateSource)
          );
          return i18n.language === "en"
            ? plateType?.labelEn || ""
            : plateType?.labelAr || "";
        },
      },
      {
        accessorKey: "plateLetter1",
        header: t("plateLetter1"),
        cell: ({ row }) => {
          const plateLetter = plateLetters?.find(
            (item) => item.value === Number(row.original.plateLetter1)
          );
          return i18n.language === "en"
            ? plateLetter?.labelEn || ""
            : plateLetter?.labelAr || "";
        },
      },
      {
        accessorKey: "plateLetter2",
        header: t("plateLetter2"),
        cell: ({ row }) => {
          const plateLetter = plateLetters?.find(
            (item) => item.value === Number(row.original.plateLetter2)
          );
          return i18n.language === "en"
            ? plateLetter?.labelEn || ""
            : plateLetter?.labelAr || "";
        },
      },
      {
        accessorKey: "plateLetter3",
        header: t("plateLetter3"),
        cell: ({ row }) => {
          const plateLetter = plateLetters?.find(
            (item) => item.value === Number(row.original.plateLetter3)
          );
          return i18n.language === "en"
            ? plateLetter?.labelEn || ""
            : plateLetter?.labelAr || "";
        },
      },
      {
        accessorKey: "plateNumber",
        header: t("plateNumber"),
      },
      {
        accessorKey: "color",
        header: t("color"),
      },
      {
        accessorKey: "driversName",
        header: t("driversName"),
        cell: ({ row }) => {
          const driversName = driversNameOptions?.find(
            (item) => item.value === row.original.driversName
          );
          return i18n.language === "en"
            ? driversName?.labelEn || ""
            : driversName?.labelAr || "";
        },
      },
      {
        header: t("action"),
        cell: ({ row }: { row: Row<Vehicle> }) => {
          return (
            <div className="flex gap-1 items-center">
              <Button
                type="button"
                onClick={() => handleEdit(row.original)}
                className="py-0.5"
              >
                {isFieldsDisabled ? t("view") : t("edit")}
              </Button>
              {!isFieldsDisabled && (
                <Button
                  onClick={() => handleDelete(row.original.id)}
                  className="bg-red-600 hover:bg-red-500 py-0.5"
                >
                  {t("delete")}
                </Button>
              )}
            </div>
          );
        },
      },
    ],
    [
      driversNameOptions,
      handleDelete,
      handleEdit,
      i18n.language,
      isFieldsDisabled,
      plateLetters,
      plateSource,
      t,
    ]
  );

  return (
    <TableWrapper
      data={addedVehicles}
      columns={columns}
      isPagination={false}
      isColumnChooser={false}
    />
  );
};

export default memo(PermitVehiclesTable);
